import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";
import {
  Container,
  SectionLabel,
  PrimaryButton,
  SecondaryButton,
  CTASection,
} from "@/components/primitives";
import { lineReveal } from "@/lib/motion";
import { EXPERIENCE_CARDS, SERVICES, WHY_CLIENTS } from "@/data/site";
import { PROJECTS } from "@/data/projects";

const HeroLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      variants={lineReveal}
      custom={i}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

function Hero() {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 600], [0, 120]);
  const y2 = useTransform(scrollY, [0, 600], [0, -80]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section className="relative min-h-screen overflow-hidden" data-testid="hero">
      {/* Parallax background layers */}
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-60" />
      <motion.div
        style={{ y: y1 }}
        className="pointer-events-none absolute -right-40 top-24 h-[520px] w-[520px] rounded-full bg-accent/20 blur-[160px]"
      />
      <motion.div
        style={{ y: y2 }}
        className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-indigo-500/10 blur-[150px]"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-ink-base to-transparent" />

      <Container className="relative flex min-h-screen flex-col justify-center pt-28 pb-20">
        <motion.div style={{ opacity }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <SectionLabel>Software Engineer · Product Builder</SectionLabel>
          </motion.div>

          <h1 className="mt-8 font-cabinet text-5xl font-medium leading-[0.98] tracking-tight text-primary-ink sm:text-6xl lg:text-8xl">
            <HeroLine i={0}>Hi, I'm</HeroLine>
            <HeroLine i={1}>Vivek Chandra.</HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 font-mono text-sm uppercase tracking-[0.2em] text-accent sm:text-base"
          >
            Software Engineer • AI Builder • Technical Consultant
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-secondary-ink md:text-xl"
          >
            I help startups and growing businesses build software that scales,
            stays maintainable and solves real business problems. With more than
            fifteen years across enterprise software, AI, product development
            and technical leadership, I turn ambitious ideas into reliable
            systems your business can depend on.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <PrimaryButton to="/projects" testId="hero-view-work">
              View My Work
            </PrimaryButton>
            <SecondaryButton to="/contact" testId="hero-work-with-me">
              Work With Me
            </SecondaryButton>
          </motion.div>
        </motion.div>
      </Container>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 md:block"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-ink">
          Scroll
        </span>
      </motion.div>
    </section>
  );
}

function ExperienceSection() {
  return (
    <section className="py-24 md:py-32" data-testid="experience-section">
      <Container>
        <Reveal className="max-w-2xl">
          <SectionLabel>Career Snapshot</SectionLabel>
          <h2 className="mt-8 font-cabinet text-3xl font-medium leading-tight tracking-tight text-primary-ink md:text-5xl">
            Fifteen years of building software that businesses depend on.
          </h2>
        </Reveal>

        <RevealGroup className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {EXPERIENCE_CARDS.map((card) => (
            <RevealItem
              key={card.title}
              className="group relative bg-ink-base p-8 transition-colors hover:bg-ink-surface md:p-10"
            >
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                <div className="absolute -inset-px bg-accent/5" />
              </div>
              <span className="relative font-cabinet text-3xl font-bold tracking-tight text-accent md:text-4xl">
                {card.stat}
              </span>
              <h3 className="relative mt-4 font-cabinet text-xl font-medium tracking-tight text-primary-ink">
                {card.title}
              </h3>
              <p className="relative mt-3 leading-relaxed text-secondary-ink">
                {card.body}
              </p>
            </RevealItem>
          ))}
        </RevealGroup>
      </Container>
    </section>
  );
}

function WhyClients() {
  return (
    <section className="py-24 md:py-32" data-testid="why-clients">
      <Container>
        <Reveal className="max-w-2xl">
          <SectionLabel>Why Clients Work With Me</SectionLabel>
          <h2 className="mt-8 font-cabinet text-3xl font-medium leading-tight tracking-tight text-primary-ink md:text-5xl">
            An engineer who understands your business.
          </h2>
        </Reveal>

        <RevealGroup className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 md:grid-cols-3">
          {WHY_CLIENTS.map((item) => (
            <RevealItem
              key={item.index}
              className="bg-ink-base p-8 md:p-10"
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
                {item.index}
              </span>
              <h3 className="mt-8 font-cabinet text-xl font-medium tracking-tight text-primary-ink md:text-2xl">
                {item.title}
              </h3>
              <p className="mt-4 leading-relaxed text-secondary-ink">
                {item.body}
              </p>
            </RevealItem>
          ))}
        </RevealGroup>
      </Container>
    </section>
  );
}

function FeaturedProjects() {
  return (
    <section className="py-24 md:py-32" data-testid="featured-projects">
      <Container>
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <SectionLabel>Featured Projects</SectionLabel>
            <h2 className="mt-8 font-cabinet text-3xl font-medium leading-tight tracking-tight text-primary-ink md:text-5xl">
              Selected work, presented as case studies.
            </h2>
          </div>
          <Link
            to="/projects"
            data-testid="view-all-projects"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-secondary-ink transition-colors hover:text-primary-ink"
          >
            All Projects
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>

        <div className="mt-16 space-y-6">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.05}>
              <Link
                to={`/projects/${p.slug}`}
                data-testid={`featured-project-${p.slug}`}
                className="group grid grid-cols-1 gap-8 border border-white/10 p-6 transition-colors hover:border-white/25 md:grid-cols-12 md:items-center md:p-8"
              >
                <div className="overflow-hidden md:col-span-5">
                  <img
                    src={p.cover}
                    alt={p.name}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                </div>
                <div className="md:col-span-7">
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-ink">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-ink">
                      {p.year}
                    </span>
                  </div>
                  <h3 className="mt-5 font-cabinet text-2xl font-medium tracking-tight text-primary-ink md:text-3xl">
                    {p.name}
                  </h3>
                  {p.tagline && (
                    <p className="mt-1 font-mono text-xs uppercase tracking-[0.12em] text-accent">
                      {p.tagline}
                    </p>
                  )}
                  <p className="mt-4 max-w-xl leading-relaxed text-secondary-ink">
                    {p.summary}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-primary-ink">
                    Read case study
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function HowICanHelp() {
  return (
    <section className="py-24 md:py-32" data-testid="how-i-can-help">
      <Container>
        <Reveal className="max-w-2xl">
          <SectionLabel>How I Can Help</SectionLabel>
          <h2 className="mt-8 font-cabinet text-3xl font-medium leading-tight tracking-tight text-primary-ink md:text-5xl">
            Engineering, focused on business outcomes.
          </h2>
        </Reveal>

        <RevealGroup className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 md:grid-cols-2">
          {SERVICES.map((s) => (
            <RevealItem
              key={s.slug}
              className="group bg-ink-base p-8 transition-colors hover:bg-ink-surface md:p-12"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-ink">
                  {s.index}
                </span>
                <ArrowUpRight className="h-5 w-5 text-muted-ink transition-all group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent" />
              </div>
              <h3 className="mt-8 font-cabinet text-2xl font-medium tracking-tight text-primary-ink md:text-3xl">
                {s.title}
              </h3>
              <p className="mt-3 leading-relaxed text-secondary-ink">
                {s.outcome}
              </p>
            </RevealItem>
          ))}
        </RevealGroup>

        <Reveal delay={0.1} className="mt-12">
          <Link
            to="/services"
            data-testid="explore-services"
            className="group inline-flex items-center gap-2 border border-white/15 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-primary-ink transition-colors hover:border-white/40 hover:bg-white/5"
          >
            Explore Services
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </Container>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title=""
        description="Vivek Chandra is a software engineer, AI builder, and technical consultant with 15+ years of experience. I help startups and businesses turn ambitious ideas into reliable, scalable software."
        path="/"
      />
      <Hero />
      <Marquee />
      <ExperienceSection />
      <WhyClients />
      <FeaturedProjects />
      <HowICanHelp />
      <CTASection />
    </>
  );
}
